/**
 * 「待办」 section: CRUD with due/remind datetime-local fields, priority,
 * completion toggle and delete confirmation.
 *
 * Rows come from AppState (validated daemon responses or the last successful
 * offline cache). Mutations are never optimistic: a failed daemon call leaves
 * the list untouched and surfaces `todoError`.
 */
import { useMemo, useState } from 'react';
import { formatClock, formatRelative } from '../lib/format';
import { nowSeconds } from '../lib/id';
import {
  TODO_PRIORITY_OPTIONS,
  emptyTodoForm,
  isTodoOverdue,
  partitionTodos,
  todoCounts,
  todoFormValuesFromTodo,
  todoPriorityLabel,
  validateTodoForm,
  type TodoDraft,
  type TodoFormErrors,
  type TodoFormValues,
} from '../lib/todo';
import type { Todo, TodoPriority } from '../lib/types';
import { useApp } from '../state/AppState';
import { ConfirmDialog } from './ConfirmDialog';
import { Icon } from './Icon';

const INPUT_CLASS =
  'w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-indigo-400/50';
const LABEL_CLASS = 'text-[11px] text-slate-400';

const PRIORITY_CLASS: Record<TodoPriority, string> = {
  high: 'border-rose-400/30 bg-rose-400/10 text-rose-200',
  normal: 'border-indigo-400/30 bg-indigo-400/10 text-indigo-200',
  low: 'border-slate-500/30 bg-slate-500/10 text-slate-300',
};

function PriorityBadge({ priority }: { priority: TodoPriority }) {
  return (
    <span className={`rounded-full border px-1.5 py-0.5 text-[9px] ${PRIORITY_CLASS[priority]}`}>
      {todoPriorityLabel(priority)}
    </span>
  );
}

function TodoForm({
  initial,
  busy,
  onSave,
  onCancel,
}: {
  initial: Todo | null;
  busy: boolean;
  onSave(draft: TodoDraft): Promise<boolean>;
  onCancel(): void;
}) {
  const [values, setValues] = useState<TodoFormValues>(() =>
    initial === null ? emptyTodoForm() : todoFormValuesFromTodo(initial),
  );
  const [errors, setErrors] = useState<TodoFormErrors>({});
  const [message, setMessage] = useState<string | null>(null);

  const update = <Key extends keyof TodoFormValues>(key: Key, value: TodoFormValues[Key]): void => {
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  const submit = async (): Promise<void> => {
    const validation = validateTodoForm(values);
    if (!validation.ok) {
      setErrors(validation.errors);
      setMessage(validation.message);
      return;
    }
    setErrors({});
    setMessage(null);
    const saved = await onSave(validation.value);
    if (!saved) setMessage('保存失败，请查看上方错误提示');
  };

  return (
    <div data-testid="todo-form" className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="space-y-2.5">
        <div>
          <label className={LABEL_CLASS} htmlFor="todo-title">
            标题
          </label>
          <input
            id="todo-title"
            type="text"
            value={values.title}
            maxLength={200}
            placeholder="要做什么？"
            onChange={(event) => update('title', event.target.value)}
            className={`mt-1 ${INPUT_CLASS}`}
          />
          {errors.title ? <p className="mt-1 text-[10px] text-rose-300">{errors.title}</p> : null}
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="todo-notes">
            备注
          </label>
          <textarea
            id="todo-notes"
            value={values.notes}
            rows={3}
            maxLength={2000}
            placeholder="可选：步骤、链接、上下文"
            onChange={(event) => update('notes', event.target.value)}
            className={`mt-1 resize-none ${INPUT_CLASS}`}
          />
          {errors.notes ? <p className="mt-1 text-[10px] text-rose-300">{errors.notes}</p> : null}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS} htmlFor="todo-due">
              截止时间
            </label>
            <input
              id="todo-due"
              type="datetime-local"
              value={values.due_local}
              onChange={(event) => update('due_local', event.target.value)}
              className={`mt-1 ${INPUT_CLASS}`}
            />
            {errors.due_local ? (
              <p className="mt-1 text-[10px] text-rose-300">{errors.due_local}</p>
            ) : null}
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="todo-remind">
              提醒时间
            </label>
            <input
              id="todo-remind"
              type="datetime-local"
              value={values.remind_local}
              onChange={(event) => update('remind_local', event.target.value)}
              className={`mt-1 ${INPUT_CLASS}`}
            />
            {errors.remind_local ? (
              <p className="mt-1 text-[10px] text-rose-300">{errors.remind_local}</p>
            ) : null}
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="todo-priority">
            优先级
          </label>
          <select
            id="todo-priority"
            value={values.priority}
            onChange={(event) => update('priority', event.target.value as TodoPriority)}
            className={`mt-1 ${INPUT_CLASS}`}
          >
            {TODO_PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.priority ? (
            <p className="mt-1 text-[10px] text-rose-300">{errors.priority}</p>
          ) : null}
        </div>

        {message ? (
          <p className="rounded-lg border border-rose-400/25 bg-rose-400/10 px-2.5 py-1.5 text-[11px] text-rose-200">
            {message}
          </p>
        ) : null}

        <div className="flex gap-2">
          <button
            type="button"
            data-testid="todo-save"
            disabled={busy}
            onClick={() => void submit()}
            className="flex-1 rounded-xl bg-indigo-500 py-2.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? '保存中…' : initial === null ? '添加待办' : '保存修改'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-slate-300 disabled:opacity-40"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
}

function TodoRow({
  todo,
  now,
  busy,
  readOnly,
  onToggle,
  onEdit,
  onDelete,
}: {
  todo: Todo;
  now: number;
  busy: boolean;
  readOnly: boolean;
  onToggle(todo: Todo): void;
  onEdit(todo: Todo): void;
  onDelete(todo: Todo): void;
}) {
  const done = todo.status === 'done';
  const overdue = isTodoOverdue(todo, now);
  return (
    <li
      data-testid="todo-row"
      data-id={todo.id}
      data-status={todo.status}
      className={`rounded-xl border px-3 py-2.5 ${
        done
          ? 'border-white/5 bg-white/[0.02]'
          : overdue
            ? 'border-rose-400/20 bg-rose-400/[0.04]'
            : 'border-white/10 bg-white/[0.03]'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <button
          type="button"
          data-testid="todo-toggle"
          aria-label={done ? '标记为未完成' : '标记为已完成'}
          aria-pressed={done}
          disabled={busy || readOnly}
          onClick={() => onToggle(todo)}
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition disabled:opacity-40 ${
            done
              ? 'border-emerald-400/50 bg-emerald-400/20 text-emerald-200'
              : 'border-white/20 bg-white/5 text-transparent'
          }`}
        >
          <Icon name="check" className="h-3 w-3" />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p
              className={`min-w-0 break-words text-sm leading-snug ${
                done ? 'text-slate-500 line-through' : 'text-slate-100'
              }`}
            >
              {todo.title}
            </p>
            <span className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                data-testid="todo-edit"
                aria-label="编辑待办"
                disabled={busy || readOnly}
                onClick={() => onEdit(todo)}
                className="rounded-lg border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-slate-300 disabled:opacity-40"
              >
                编辑
              </button>
              <button
                type="button"
                data-testid="todo-delete"
                aria-label="删除待办"
                disabled={busy || readOnly}
                onClick={() => onDelete(todo)}
                className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-2 py-0.5 text-[10px] text-rose-100 disabled:opacity-40"
              >
                删除
              </button>
            </span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <PriorityBadge priority={todo.priority} />
            {todo.due_at !== null ? (
              <span
                className={`rounded-full border px-1.5 py-0.5 text-[9px] ${
                  overdue
                    ? 'border-rose-400/30 bg-rose-400/10 text-rose-200'
                    : 'border-white/10 bg-white/5 text-slate-400'
                }`}
              >
                {overdue ? '已逾期 · ' : '截止 '}
                {formatClock(todo.due_at)}
              </span>
            ) : null}
            {todo.remind_at !== null ? (
              <span className="rounded-full border border-amber-400/25 bg-amber-400/10 px-1.5 py-0.5 text-[9px] text-amber-200">
                提醒 {formatClock(todo.remind_at)} · {formatRelative(todo.remind_at, now)}
              </span>
            ) : null}
            {done && todo.completed_at !== null ? (
              <span className="text-[9px] text-slate-500">
                完成于 {formatClock(todo.completed_at)}
              </span>
            ) : null}
          </div>

          {todo.notes ? (
            <p className="mt-1.5 whitespace-pre-wrap break-words text-[11px] leading-relaxed text-slate-400">
              {todo.notes}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function TodoGroup({
  title,
  todos,
  now,
  busyId,
  readOnly,
  onToggle,
  onEdit,
  onDelete,
}: {
  title: string;
  todos: readonly Todo[];
  now: number;
  busyId: string | null;
  readOnly: boolean;
  onToggle(todo: Todo): void;
  onEdit(todo: Todo): void;
  onDelete(todo: Todo): void;
}) {
  if (todos.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 pb-1">
        <h3 className="text-[11px] font-medium text-slate-300">{title}</h3>
        <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
          {todos.length}
        </span>
        <span className="h-px flex-1 bg-white/5" />
      </div>
      <ul className="space-y-2">
        {todos.map((todo) => (
          <TodoRow
            key={todo.id}
            todo={todo}
            now={now}
            busy={busyId === todo.id}
            readOnly={readOnly}
            onToggle={onToggle}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </div>
  );
}

export function TodoSection() {
  const {
    todoAlarmAvailable,
    todoAlarmUnavailableReason,
    todos,
    todoSyncedAt,
    todoLoading,
    todoError,
    refreshTodos,
    createTodo,
    updateTodo,
    deleteTodo,
    toggleTodo,
    network,
  } = useApp();
  const [formMode, setFormMode] = useState<'closed' | 'create' | 'edit'>('closed');
  const [editing, setEditing] = useState<Todo | null>(null);
  const [deleting, setDeleting] = useState<Todo | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const now = nowSeconds();
  const counts = todoCounts(todos, now);
  const { open, done } = useMemo(() => partitionTodos(todos, now), [todos, now]);
  const readOnly = !todoAlarmAvailable;

  const save = async (draft: TodoDraft): Promise<boolean> => {
    const result =
      formMode === 'edit' && editing !== null
        ? await updateTodo(editing.id, draft)
        : await createTodo(draft);
    if (!result.ok) return false;
    setFormMode('closed');
    setEditing(null);
    return true;
  };

  const toggle = async (todo: Todo): Promise<void> => {
    if (readOnly) return;
    setBusyId(todo.id);
    await toggleTodo(todo.id);
    setBusyId(null);
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleting === null) return;
    setBusyId(deleting.id);
    const result = await deleteTodo(deleting.id);
    setBusyId(null);
    if (result.ok) setDeleting(null);
  };

  const cacheLabel =
    todoSyncedAt === null
      ? null
      : `上次同步 ${formatClock(todoSyncedAt)}${
          !network.online || todoError !== null ? ' · 离线缓存，可能不是最新' : ''
        }`;

  return (
    <div className="space-y-3">
      <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-100">待办</h2>
            <p className="mt-0.5 text-[11px] text-slate-500">
              进行中 {counts.open}
              {counts.overdue > 0 ? ` · 逾期 ${counts.overdue}` : ''} · 已完成 {counts.done}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              data-testid="todo-refresh"
              disabled={!todoAlarmAvailable || todoLoading}
              onClick={() => void refreshTodos()}
              className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300 disabled:opacity-40"
            >
              <Icon name="refresh" className={`h-3 w-3 ${todoLoading ? 'animate-spin' : ''}`} />
              刷新
            </button>
            <button
              type="button"
              data-testid="todo-new"
              disabled={!todoAlarmAvailable || formMode !== 'closed'}
              onClick={() => {
                setEditing(null);
                setFormMode('create');
              }}
              className="flex items-center gap-1 rounded-full border border-indigo-400/40 bg-indigo-400/15 px-2.5 py-1 text-[11px] text-indigo-100 disabled:opacity-40"
            >
              <Icon name="plus" className="h-3 w-3" />
              新建
            </button>
          </div>
        </div>

        {!todoAlarmAvailable ? (
          <p
            data-testid="todo-unavailable"
            className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200"
          >
            {todoAlarmUnavailableReason ?? '待办当前不可用'}
            {todos.length > 0 ? ' 下方为只读缓存。' : ''}
          </p>
        ) : null}

        {todoError ? (
          <div
            data-testid="todo-error"
            className="mt-3 rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200"
          >
            <p className="break-words">{todoError}</p>
            {todoAlarmAvailable ? (
              <button
                type="button"
                onClick={() => void refreshTodos()}
                disabled={todoLoading}
                className="mt-2 rounded-lg border border-rose-400/40 bg-rose-400/10 px-2.5 py-1 text-[11px] text-rose-100 disabled:opacity-40"
              >
                重试
              </button>
            ) : null}
          </div>
        ) : null}

        {formMode !== 'closed' ? (
          <div className="mt-3">
            <TodoForm
              key={formMode === 'edit' ? `edit-${editing?.id ?? ''}` : 'create'}
              initial={formMode === 'edit' ? editing : null}
              busy={todoLoading}
              onSave={save}
              onCancel={() => {
                if (todoLoading) return;
                setFormMode('closed');
                setEditing(null);
              }}
            />
          </div>
        ) : null}

        <div className="mt-3 space-y-3">
          {todoLoading && todos.length === 0 ? (
            <p className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-4 text-center text-[11px] text-slate-500">
              正在读取待办…
            </p>
          ) : null}

          {!todoLoading && todos.length === 0 && todoAlarmAvailable ? (
            <section
              data-testid="todo-empty"
              className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-6 text-center"
            >
              <Icon name="todo" className="mx-auto h-7 w-7 text-slate-600" />
              <p className="mt-2 text-sm text-slate-300">还没有待办</p>
              <p className="mt-1 text-[11px] text-slate-500">
                新建一条待办，设置截止与提醒时间；数据保存在 daemon 本机。
              </p>
            </section>
          ) : null}

          <TodoGroup
            title="进行中"
            todos={open}
            now={now}
            busyId={busyId}
            readOnly={readOnly}
            onToggle={(todo) => void toggle(todo)}
            onEdit={(todo) => {
              if (readOnly) return;
              setEditing(todo);
              setFormMode('edit');
            }}
            onDelete={(todo) => {
              if (readOnly) return;
              setDeleting(todo);
            }}
          />
          <TodoGroup
            title="已完成"
            todos={done}
            now={now}
            busyId={busyId}
            readOnly={readOnly}
            onToggle={(todo) => void toggle(todo)}
            onEdit={(todo) => {
              if (readOnly) return;
              setEditing(todo);
              setFormMode('edit');
            }}
            onDelete={(todo) => {
              if (readOnly) return;
              setDeleting(todo);
            }}
          />
        </div>

        {cacheLabel ? <p className="mt-3 text-[10px] text-slate-500">{cacheLabel}</p> : null}
      </section>

      <ConfirmDialog
        open={deleting !== null}
        title="删除这条待办？"
        danger
        busy={todoLoading}
        confirmLabel="删除"
        onCancel={() => {
          if (todoLoading) return;
          setDeleting(null);
        }}
        onConfirm={() => void confirmDelete()}
      >
        <p className="text-[11px] leading-relaxed text-slate-400">
          「{deleting?.title ?? ''}」将从 daemon 中删除，无法恢复。
        </p>
      </ConfirmDialog>
    </div>
  );
}
